import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { AppProvider } from "@/contexts/AppContext";
import { AuthProvider } from "@/contexts/AuthContext";

// Main travel site
import Index, { AppLayout } from "./pages/Index";
import AlbanianTravel from "./pages/AlbanianTravel";
import LuxuryTravel from "./pages/LuxuryTravel";
import CorporateTravel from "./pages/CorporateTravel";
import YachtCharters from "./pages/YachtCharters";
import VillaRentals from "./pages/VillaRentals";
import TravelBlog from "./pages/TravelBlog";
import Booking from "./pages/Booking";
import Profile from "./pages/Profile";
import ResetPassword from "./pages/ResetPassword";
import InstagramContent from "./pages/InstagramContent";
import RomaniaDestinations from "./pages/romania/RomaniaDestinations";
import RomaniaExperiences from "./pages/romania/RomaniaExperiences";
import RomaniaJournal from "./pages/romania/RomaniaJournal";

// Admin / backend
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminHub";
import ContentPlanner from "./pages/admin/ContentPlanner";
import AIToolsDashboard from "./pages/AIToolsDashboard";
import TrainingAcademyPage from "./pages/admin/TrainingAcademy";

// The MTN Shop — standalone AI Social Media Hub
import SocialPopAdmin from "./pages/mtnshop/SocialPopAdmin";


import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

/**
 * Zera Travel — full site routing.
 *
 * Public site lives at `/` and friends, while the operational backend tools
 * (admin CRM, content planner, AI Social Tools dashboard) live under the
 * admin namespace and are linked from the Admin Dashboard.
 */
const App = () => (
  <ThemeProvider defaultTheme="light">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <AppProvider>
            <BrowserRouter>
              <Toaster />
              <Sonner />
              <Routes>
                {/* Public travel site */}
                <Route path="/" element={<Index />} />

                {/* Per-country full sites — same layout, parametrised by country */}
                <Route path="/albania" element={<AppLayout country="albania" />} />
                <Route path="/romania" element={<AppLayout country="romania" />} />

                {/* Romania-only dedicated pages */}
                <Route path="/romania/destinations" element={<RomaniaDestinations />} />
                <Route path="/romania/experiences" element={<RomaniaExperiences />} />
                <Route path="/romania/journal" element={<RomaniaJournal />} />
                <Route path="/romania/booking" element={<Booking country="romania" />} />


                <Route path="/albanian-travel" element={<AlbanianTravel />} />
                <Route path="/luxury-travel" element={<LuxuryTravel />} />
                <Route path="/corporate-travel" element={<CorporateTravel />} />
                <Route path="/yacht-charters" element={<YachtCharters />} />
                <Route path="/villa-rentals" element={<VillaRentals />} />
                <Route path="/blog" element={<TravelBlog />} />
                <Route path="/booking" element={<Booking />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/instagram-content" element={<InstagramContent />} />

                {/*
                  Backend / admin — ONE canonical URL per page (no duplicates).
                  AdminDashboard already exposes Instagram Content & Brand Studio
                  as tabs, so it only needs a single /albaniaadmin/dashboard route.
                */}
                <Route path="/albaniaadmin" element={<AdminLogin />} />
                <Route path="/albaniaadmin/dashboard" element={<AdminDashboard />} />
                <Route path="/albaniaadmin/content-planner" element={<ContentPlanner />} />
                <Route path="/albaniaadmin/ai-tools" element={<AIToolsDashboard />} />
                <Route path="/albaniaadmin/training" element={<TrainingAcademyPage />} />

                {/* Legacy aliases now just redirect to the canonical route so
                    old bookmarks/links don't 404 and no duplicate pages appear. */}
                <Route path="/albaniaadmin/instagram" element={<Navigate to="/albaniaadmin/dashboard" replace />} />
                <Route path="/albaniaadmin/brand-studio" element={<Navigate to="/albaniaadmin/dashboard" replace />} />
                <Route path="/mtnadmin" element={<Navigate to="/albaniaadmin" replace />} />
                <Route path="/mtnadmin/*" element={<Navigate to="/albaniaadmin/dashboard" replace />} />
                <Route path="/admin" element={<Navigate to="/albaniaadmin/dashboard" replace />} />
                <Route path="/admin/instagram" element={<Navigate to="/albaniaadmin/dashboard" replace />} />
                <Route path="/admin/brand-studio" element={<Navigate to="/albaniaadmin/dashboard" replace />} />
                <Route path="/admin/content-planner" element={<Navigate to="/albaniaadmin/content-planner" replace />} />
                <Route path="/admin/ai-tools" element={<Navigate to="/albaniaadmin/ai-tools" replace />} />
                <Route path="/ai-tools" element={<Navigate to="/albaniaadmin/ai-tools" replace />} />

                {/*
                  Romania backend (/romaniaadmin) — a complete copy of /albaniaadmin
                  wired to the Romania data realm. Same page components; the
                  active realm is set at login and used to scope all reads/writes.
                */}
                <Route path="/romaniaadmin" element={<AdminLogin />} />
                <Route path="/romaniaadmin/dashboard" element={<AdminDashboard />} />
                <Route path="/romaniaadmin/content-planner" element={<ContentPlanner />} />
                <Route path="/romaniaadmin/ai-tools" element={<AIToolsDashboard />} />
                <Route path="/romaniaadmin/training" element={<TrainingAcademyPage />} />
                <Route path="/romaniaadmin/instagram" element={<Navigate to="/romaniaadmin/dashboard" replace />} />
                <Route path="/romaniaadmin/brand-studio" element={<Navigate to="/romaniaadmin/dashboard" replace />} />
                <Route path="/zeraadmin" element={<Navigate to="/romaniaadmin" replace />} />
                <Route path="/zeraadmin/*" element={<Navigate to="/romaniaadmin/dashboard" replace />} />


                {/*
                  Lewith & Freeman Real Estate (/socialschool) — fully
                  white-labeled deployment of the same admin platform, wired to
                  the isolated `lewith` data realm. Branding comes from
                  src/lib/adminBrand.ts.
                  Login: LewithFreemanAdmin / LewithFreemanAdmin
                */}
                <Route path="/socialschool" element={<AdminLogin />} />
                <Route path="/socialschool/dashboard" element={<AdminDashboard />} />
                <Route path="/socialschool/content-planner" element={<ContentPlanner />} />
                <Route path="/socialschool/ai-tools" element={<AIToolsDashboard />} />
                <Route path="/socialschool/training" element={<TrainingAcademyPage />} />
                <Route path="/socialschool/instagram" element={<Navigate to="/socialschool/dashboard" replace />} />
                <Route path="/socialschool/brand-studio" element={<Navigate to="/socialschool/dashboard" replace />} />

                {/*
                  The MTN Shop — AI Social Media Hub (standalone build).
                  Login: ShopSocialAdmin / ShopSocialAdmin
                  React Router matching is case-insensitive, so /SocialPopAdmin works.
                */}

                <Route path="/socialpopadmin" element={<SocialPopAdmin />} />


                <Route path="*" element={<NotFound />} />
              </Routes>
            </BrowserRouter>
          </AppProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
